import { AreaPageListing, AreaPageListingResponse, SimilarListing, StandardStatus } from '@zoocasa/go-search';
import { HttpRequestMethodTypes } from 'types';
import { FetchOptions } from 'types/fetchWithRetry';
import { fetchWithRetry } from 'utils/fetchWithRetry';
import { getInsightsHost, getGoSearchHost, isServerSide } from 'utils/host-config';
import {
  SimilarListingsRequest,
  SimilarListingsResponse,
  LocationSimilarListingsRequest,
} from '@zoocasa/go-search';
import type { Point } from '../client-app/types/point';

//#region types

/**
 * Parameters for fetching similar listings by location.
 */
export type GetSimilarListingsByLocationParams = {
  /** The position of the reference listing */
  position: Point;
  /** The number of similar listings to return */
  size: number;
  /** The type of home */
  homeType: string;
  /** The number of bedrooms filter */
  bedrooms: number;
  /** The number of bathrooms filter */
  bathrooms: number;
  /** The square footage filter */
  sqft: number;
  /** Optional filter by listing status */
  standardStatus?: StandardStatus;
  /** Optional minimum price filter */
  minPrice?: number;
  /** Optional maximum price filter */
  maxPrice?: number;
  /** Optional filter for rental listings */
  isRental?: boolean;
};

/**
 * Parameters for fetching similar listings.
 */
export type GetSimilarListingsParams = {
  /** The unique identifier of the reference listing */
  id: string | number;
  /** The number of similar listings to return */
  size: number;
  /** Optional filter by listing status */
  standardStatus?: StandardStatus;
  /** Optional minimum price filter */
  minPrice?: number;
  /** Optional maximum price filter */
  maxPrice?: number;
};

//#endregion

/**
 * Fetches listing for the given MLS numbers.
 *
 * @param mls Array of MLS numbers to search for
 * @param limit Maximum number of listings to return. Defaults to 15.
 * @param signal Optional {@link AbortSignal} to cancel the request
 * @param host The host URL for the API request. Defaults to the go-search host based on server-side detection.
 * @returns Promise that resolves to an array of {@link AreaPageListing} objects, or empty array if request fails
 * @throws When the API request fails or response cannot be decoded (returns empty array instead of throwing)
 */
export async function getListingByMls(mls: string[], limit = 15, signal?: AbortSignal, host: string = getGoSearchHost(isServerSide())): Promise<AreaPageListing[]> {
  const searchMlsUrl = `${host}/api/search-mls?mls=${mls}&limit=${limit}`;
  const options: FetchOptions = { method: HttpRequestMethodTypes.GET, signal };

  try {
    const response = await fetchWithRetry(searchMlsUrl, options);
    if (response.ok) {
      const content = await response.blob();
      const buffer = await content.arrayBuffer();
      const apiResp = AreaPageListingResponse.decode(new Uint8Array(buffer));
      return apiResp?.data || [];
    }
    console.error(`fetch listings by MLS: ${response.status} ${response.statusText} ${response.type}`);
    return [];
  } catch (error: unknown) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.debug('fetch listings request was aborted');
    } else {
      console.error('failed to fetch listings by MLS: %s', error);
    }
    return [];
  }
}

/**
 * Fetches listings for the given listing IDs.
 *
 * @param ids Array of listing IDs to search for
 * @param limit Maximum number of listings to return. Defaults to 100.
 * @param signal Optional {@link AbortSignal} to cancel the request
 * @param host The host URL for the API request. Defaults to the go-search host based on server-side detection.
 * @returns Promise that resolves to an array of {@link AreaPageListing} objects, or empty array if request fails
 * @throws When the API request fails or response cannot be decoded (returns empty array instead of throwing)
 */
export async function getListingsByIds(ids: number[], limit = 100, signal?: AbortSignal, host: string = getGoSearchHost(isServerSide())): Promise<AreaPageListing[]> {
  const searchIdsPageUrl = `${host}/api/search-ids?ids=${ids}&limit=${limit}`;
  const options: FetchOptions = { method: HttpRequestMethodTypes.GET, signal };

  try {
    const response = await fetchWithRetry(searchIdsPageUrl, options);
    if (response.ok) {
      const content = await response.blob();
      const buffer = await content.arrayBuffer();
      const apiResp = AreaPageListingResponse.decode(new Uint8Array(buffer));
      return apiResp?.data || [];
    }

    console.error(`fetch listings by IDs: ${response.status} ${response.statusText} ${response.type}`);
    return [];
  } catch (error: unknown) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.debug('fetch listings request was aborted');
    } else {
      console.error('failed to fetch listings by IDs: %s', error);
    }
    return [];
  }
}

/**
 * Fetches similar listings based on a reference listing ID.
 *
 * This function retrieves listings that are similar to a given listing.
 * The results can be filtered by status, price range, and other criteria.
 *
 * @param params The {@link GetSimilarListingsParams parameters} for the similar listings search.
 *   - `id`: The unique identifier of the reference listing (can be string or number)
 *   - `size`: The number of similar listings to return
 *   - `standardStatus`: Optional filter by listing status (e.g., 'available', 'sold')
 *   - `minPrice`: Optional minimum price filter
 *   - `maxPrice`: Optional maximum price filter
 * @param signal Optional {@link AbortSignal} to cancel the request
 * @param host The host URL for the insights API request. Defaults to the insights host based on server-side detection.
 *
 * @returns Promise that resolves to an array of {@link SimilarListing} objects, or empty array if request fails
 * @throws When the API request fails or response cannot be decoded (returns empty array instead of throwing)
 *
 * @example
 * // Get 10 similar listings for a property
 * const similarListings = await getSimilarListings({
 *   id: '12345',
 *   size: 10
 * });
 *
 * @example
 * // Get similar listings with price filter
 * const similarListings = await getSimilarListings({
 *   id: '12345',
 *   size: 5,
 *   minPrice: 500000,
 *   maxPrice: 1000000,
 *   standardStatus: StandardStatus.STANDARD_STATUS_ACTIVE
 * });
 *
 * @example
 * // With abort signal for request cancellation
 * const controller = new AbortController();
 * const similarListings = await getSimilarListings({
 *   id: '12345',
 *   size: 8
 * }, controller.signal);
 */
export async function getSimilarListings(params: GetSimilarListingsParams, signal?: AbortSignal, host: string = getInsightsHost(isServerSide())): Promise<SimilarListing[]> {
  const { id, size, standardStatus, minPrice, maxPrice } = params;
  const similarListingsUrl = `${host}/insights/listings/${id}/similar`;

  try {
    const similarListingsRequest = SimilarListingsRequest.fromPartial({ pageSize: size, standardStatus, minPrice, maxPrice });
    const requestBody = SimilarListingsRequest.encode(similarListingsRequest).finish();
    const options: FetchOptions = { method: HttpRequestMethodTypes.POST, body: requestBody, signal };
    const response = await fetchWithRetry(similarListingsUrl, options);
    if (response.ok) {
      const content = await response.blob();
      const buffer = await content.arrayBuffer();
      const resp: SimilarListingsResponse = SimilarListingsResponse.decode(new Uint8Array(buffer));
      return resp.data;
    }
    console.error(`fetch similar listings: ${response.status} ${response.statusText} ${response.type}`);
    return [];
  } catch (error: unknown) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.debug('fetch listings request was aborted');
    } else {
      console.error('failed to fetch listings by IDs: %s', error);
    }
    return [];
  }
}

/**
 * Fetches similar listings based on location and property characteristics.
 *
 * This function retrieves listings that are similar to a reference property based on
 * geographic location and property attributes like bedrooms, bathrooms, square footage,
 * and home type. The search uses the property's coordinates to find nearby listings
 * with similar characteristics.
 *
 * @param params The {@link GetSimilarListingsByLocationParams parameters} for the location-based search.
 *   - `position`: The geographic coordinates of the reference property
 *   - `size`: The number of similar listings to return
 *   - `homeType`: The type of home (e.g., 'house', 'condo', 'townhouse')
 *   - `bedrooms`: The number of bedrooms filter
 *   - `bathrooms`: The number of bathrooms filter
 *   - `sqft`: The square footage filter
 *   - `standardStatus`: Optional filter by listing status (defaults to active listings)
 *   - `minPrice`: Optional minimum price filter
 *   - `maxPrice`: Optional maximum price filter
 *   - `isRental`: Optional filter for rental listings
 * @param signal Optional {@link AbortSignal} to cancel the request
 * @param host The host URL for the insights API request. Defaults to the go-search host based on server-side detection.
 *
 * @returns Promise that resolves to an array of {@link SimilarListing} objects, or empty array if request fails
 * @throws When the API request fails or response cannot be decoded (returns empty array instead of throwing)
 *
 * @example
 * // Get similar listings for a 3-bedroom house
 * const similarListings = await getSimilarListingsByLocation({
 *   position: new Point({
 *     type: 'Point',
 *     coordinates: [-79.3832, 43.6532],
 *   }),
 *   size: 10,
 *   homeType: 'house',
 *   bedrooms: 3,
 *   bathrooms: 2,
 *   sqft: 1500
 * });
 *
 * @example
 * // Get similar listings with price filter
 * const similarListings = await getSimilarListingsByLocation({
 *   position: new Point({
 *     type: 'Point',
 *     coordinates: [-79.3832, 43.6532],
 *   }),
 *   size: 5,
 *   homeType: 'condo',
 *   bedrooms: 2,
 *   bathrooms: 1,
 *   sqft: 1000,
 *   minPrice: 500000,
 *   maxPrice: 800000,
 *   standardStatus: StandardStatus.STANDARD_STATUS_ACTIVE
 * });
 *
 * @example
 * // With abort signal for request cancellation
 * const controller = new AbortController();
 *
 * const similarListings = await getSimilarListingsByLocation({
 *   position: new Point({
 *     type: 'Point',
 *     coordinates: [-79.3832, 43.6532],
 *   }),
 *   size: 8,
 *   homeType: 'townhouse',
 *   bedrooms: 4,
 *   bathrooms: 3,
 *   sqft: 2000
 * }, controller.signal);
 */
export async function getSimilarListingsByLocation(params: GetSimilarListingsByLocationParams, signal?: AbortSignal, host: string = getGoSearchHost(isServerSide())): Promise<SimilarListing[]> {
  const { position, size, homeType, bedrooms, bathrooms, sqft, standardStatus = StandardStatus.STANDARD_STATUS_ACTIVE, minPrice, maxPrice } = params;
  const url = `${host}/insights/similar`;

  try {
    const requestObject = {
      position: {
        type: 'Point',
        coordinates: [...position.coordinates],
      },
      homeType,
      bedrooms,
      bathrooms,
      sqft,
      size,
      standardStatus,
      minPrice,
      maxPrice,
    };
    const request = LocationSimilarListingsRequest.fromPartial(requestObject);
    const requestBody = LocationSimilarListingsRequest.encode(request).finish();
    const options: FetchOptions = { method: HttpRequestMethodTypes.POST, body: requestBody, signal };
    const response = await fetchWithRetry(url, options);
    if (response.ok) {
      const content = await response.blob();
      const buffer = await content.arrayBuffer();
      const resp: SimilarListingsResponse = SimilarListingsResponse.decode(new Uint8Array(buffer));
      return resp.data;
    }
    console.error(`fetch similar listings by location: ${response.status} ${response.statusText} ${response.type}`);
    return [];
  } catch (error: unknown) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.debug('fetch similar listings by location request was aborted');
    } else {
      console.error('failed to fetch similar listings by location: %s', error);
    }
    return [];
  }
}